/**
 * Drives the in-app "Where did you hear about us?" prompt (WhereHeardPrompt,
 * native sheet + web dialog). See lib/where-heard-prompt.ts for the decision.
 *
 * The answer is saved exactly where the onboarding step saved it:
 * PUT /api/onboarding/preferences `{ whereHeard }` through
 * usePersistOnboardingPreferences (sectionSettings.onboarding on the server,
 * plus the `onboarding_preferences_saved` analytics), and the same
 * `onboarding_where_heard` product event, with `placement: 'my_tabbio_cv'`.
 * A skip saves only `whereHeardAskedAt`, so no device asks again.
 */
import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useIsFocused } from "@react-navigation/native";
import { useAuth } from "./useAuth";
import { usePersistOnboardingPreferences } from "./usePersistOnboardingPreferences";
import { getOnboardingState } from "../lib/onboarding-api";
import { trackProductEvent } from "../lib/product-analytics";
import {
  WHERE_HEARD_PROMPT_OPEN_DELAY_MS,
  clearWhereHeardPromptRequest,
  hasPendingWhereHeardPromptRequest,
  hasWhereHeardBeenAsked,
  markWhereHeardAsked,
  shouldShowWhereHeardPrompt,
} from "../lib/where-heard-prompt";
import type {
  WhereHeardOption,
  WhereHeardPlacement,
} from "../components/onboarding/where-heard-options";

export const WHERE_HEARD_PROMPT_PLACEMENT: WhereHeardPlacement = "my_tabbio_cv";

export function useWhereHeardPrompt() {
  const { user } = useAuth();
  const accountId = user?.id ?? null;
  const isFocused = useIsFocused();
  // Read once per mount: My Tabbio stamps the request right before it pushes
  // the CV route, and only that CV screen may consume it.
  const [requested] = useState(() => hasPendingWhereHeardPromptRequest());
  const [askedLocally, setAskedLocally] = useState(() =>
    hasWhereHeardBeenAsked(accountId),
  );
  const [visible, setVisible] = useState(false);
  // Stays true through the close animation so the native sheet can slide out.
  const [mounted, setMounted] = useState(false);
  const [selected, setSelected] = useState<WhereHeardOption | null>(null);

  const needsServerCheck = requested && !!accountId && !askedLocally;
  const stateQuery = useQuery({
    queryKey: ["onboarding", "state", "where-heard", accountId],
    queryFn: getOnboardingState,
    enabled: needsServerCheck,
    staleTime: 5 * 60_000,
    retry: false,
  });

  useEffect(() => {
    if (!needsServerCheck || !isFocused) return;
    if (stateQuery.isPending) return;
    const serverState = stateQuery.isError ? null : (stateQuery.data ?? null);
    const show = shouldShowWhereHeardPrompt({
      requested,
      accountId,
      askedLocally,
      serverState,
    });
    if (!show) {
      // Answered or skipped elsewhere already: remember that here too, so the
      // next open costs nothing. A failed check leaves the request to expire.
      if (serverState) {
        markWhereHeardAsked(accountId);
        setAskedLocally(true);
      }
      clearWhereHeardPromptRequest();
      return;
    }
    const timer = setTimeout(() => {
      clearWhereHeardPromptRequest();
      setMounted(true);
      setVisible(true);
    }, WHERE_HEARD_PROMPT_OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [
    accountId,
    askedLocally,
    isFocused,
    needsServerCheck,
    requested,
    stateQuery.data,
    stateQuery.isError,
    stateQuery.isPending,
  ]);

  const { isPersistingPreferences, persistOnboardingPreferences } =
    usePersistOnboardingPreferences({ step: "where-heard" });

  const finish = useCallback(() => {
    markWhereHeardAsked(accountId);
    setAskedLocally(true);
    setVisible(false);
  }, [accountId]);

  const answer = useCallback(
    async (option: WhereHeardOption) => {
      if (isPersistingPreferences) return;
      setSelected(option);
      trackProductEvent("onboarding_where_heard", {
        source: option,
        placement: WHERE_HEARD_PROMPT_PLACEMENT,
      });
      // Same save path as the onboarding step; a failure is already reported
      // by the hook, and the user is never kept waiting on their own CV.
      await persistOnboardingPreferences({
        whereHeard: option,
        whereHeardAskedAt: new Date().toISOString(),
      });
      finish();
    },
    [finish, isPersistingPreferences, persistOnboardingPreferences],
  );

  const skip = useCallback(() => {
    if (isPersistingPreferences) return;
    trackProductEvent("onboarding_where_heard_skipped", {
      placement: WHERE_HEARD_PROMPT_PLACEMENT,
    });
    finish();
    void persistOnboardingPreferences({
      whereHeardAskedAt: new Date().toISOString(),
    });
  }, [finish, isPersistingPreferences, persistOnboardingPreferences]);

  const onAfterClose = useCallback(() => {
    setMounted(false);
    setSelected(null);
  }, []);

  return {
    visible,
    mounted,
    selected,
    isSaving: isPersistingPreferences,
    answer,
    skip,
    onAfterClose,
  };
}
